import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig} from '@angular/material/legacy-dialog';

import {AuthenticatedUserService} from '@app/core/service/authenticated-user.service';
import {
    DirectoryUser,
    User
} from '@app/core/model/user';
import {VerifyPhoneDialogComponent, SmsMfaDialogViews} from '@app/core/comp/dialog/verify-phone-dialog/verify-phone-dialog.component';
import {copyData} from '@app/core/model/integration-models';
import {IntegrationService} from '@app/core/service/integration.service';
import {DirectoryOrganization} from '@app/core/model/entity-profile';
import {environment} from '@env/environment';
import {ChangePasswordDialogComponent} from '@app/core/comp/dialog/change-password-dialog/change-password-dialog.component';
import {ThemeService} from '@app/core/service/theme.service';

@Component({
    selector: 'ngc-user-security-settings',
    styleUrls: ['user-security-settings.scss'],
    templateUrl: 'user-security-settings.html'
})
export class UserSecuritySettingsComponent implements OnInit, OnDestroy {

    @Input() userData: DirectoryUser;
    @Input() currentOrg: DirectoryOrganization;
    @Input() currentUser: User;
    @Input() isTabActive: boolean;
    @Input() isUserAuthorizedViaSchoolPassport: boolean;
    @Input() disableMFAConfiguration: boolean;
    @Input() userIsMissingOnETX: boolean;

    @Output() startSpinner: EventEmitter<boolean> = new EventEmitter();

    hasRecoveryCodes: boolean;
    infoMsg: string = null;
    readonly customerShortName = environment.customer.customerShortName;

    private unsubscribe$ = new Subject<void>();

    constructor(
        public authenticatedUserService: AuthenticatedUserService,
        public integrationService: IntegrationService,
        private matDialog: MatDialog,
        private themeService: ThemeService
    ) {
    }

    ngOnInit() {
        if ((this.currentUser.mfaEnabled !== this.userData.mfaEnabled)
            || ((this.currentUser.mfaPhone || this.userData.mfaPhone) && (this.currentUser.mfaPhone !== this.userData.mfaPhone))) {
            this.authenticatedUserService.refreshAuthenticatedUser();
        }

        this.loadInfoMsg();
    }

    private loadInfoMsg(): void {
        const key = 'custom.my-profile-security-settings-info-msg';
        this.themeService.getBundle()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(bundle => {
                if (bundle) {
                    this.infoMsg = this.themeService.getValueOfTarget(bundle, key);
                }
            });
    }

    onChangePassword(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.width = '570px';
        dialogConfig.data = {
            user: copyData(this.currentUser)
        };
        dialogConfig.disableClose = true;

        const dialogRef = this.matDialog.open(ChangePasswordDialogComponent, dialogConfig);

        dialogRef.afterClosed().pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => { });
    }

    onRegenerateRecoveryCodes(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.width = '570px';
        dialogConfig.data = {
            user: copyData(this.currentUser),
            view: SmsMfaDialogViews.VerifyPassword
        };
        dialogConfig.disableClose = true;

        const dialogRef = this.matDialog.open(VerifyPhoneDialogComponent, dialogConfig);

        dialogRef.afterClosed().pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => { });
    }

    redirectToSchoolPassport() {
        const isSysAdmin = this.authenticatedUserService.isSysAdmin(this.currentUser);
        window.open(`${environment.etxBaseURI}/account/${isSysAdmin ? 'sysadm' : 'default'}/login-form`);
    }

    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
